import React, { useState, useEffect } from 'react';
import { useImageCache } from '@/context/ImageCacheContext';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, Link2, ShoppingCart, Plus, ChevronRight, Wrench, AlertCircle, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { MrpEffectiveDate } from '@/components/MrpChangeBadge';
import { formatJpWithAlias } from '@/utils/partDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Product Image Component
const ProductImage = ({ filename, className = "w-16 h-16" }) => {
  const { getImageUrl } = useImageCache();
  const [imageError, setImageError] = useState(false);
  const imageUrl = getImageUrl(filename);

  if (!filename || imageError) {
    return (
      <div className={`${className} rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0`}>
        <Package className="w-6 h-6 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt=""
      className={`${className} rounded-lg object-cover flex-shrink-0`}
      onError={() => setImageError(true)}
    />
  );
};

const RelatedParts = ({ jpPartNo, partName, className = "" }) => {
  const [relatedParts, setRelatedParts] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const { addToCart } = useCart();
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchRelatedParts = async () => {
      if (!jpPartNo) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/parts-linking/related/${jpPartNo}`);
        setRelatedParts(response.data.related_parts || []);
        setMessage(response.data.message || '');
        setGroups(response.data.groups || []);
      } catch (error) {
        console.error('Error fetching related parts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedParts();
  }, [jpPartNo]);

  const handleAddToCart = (part) => {
    if (!user || !token) {
      toast.error('Please login to add items to cart');
      return;
    }
    
    addToCart({
      jp_part_no: part.jp_part_no,
      part_name: part.part_name,
      mrp: part.mrp
    }, 1);
    
    toast.success(`${part.part_name} added to cart`);
  };

  // Don't render anything if no related parts
  if (loading || relatedParts.length === 0) {
    return null;
  }

  // Witty messages - different styles rotating based on time
  const wittyStyles = [
    { icon: Wrench, color: "text-orange-500", bg: "bg-orange-50", border: "border-orange-200",
      title: "Complete the Repair", 
      subtitle: "These parts often need replacing together" },
    { icon: Zap, color: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200",
      title: "Often Bought Together", 
      subtitle: "Customers who bought this also got these" },
    { icon: AlertCircle, color: "text-amber-500", bg: "bg-amber-50", border: "border-amber-200",
      title: "Don't Forget These!", 
      subtitle: "If this is worn out, check these too" },
    { icon: Package, color: "text-green-500", bg: "bg-green-50", border: "border-green-200",
      title: "Complete Kit", 
      subtitle: "Get the full set for best results" },
    { icon: Wrench, color: "text-purple-500", bg: "bg-purple-50", border: "border-purple-200",
      title: "Pro Mechanic's Choice", 
      subtitle: "Experienced mechanics replace these together" },
    { icon: Zap, color: "text-red-500", bg: "bg-red-50", border: "border-red-200",
      title: "Save a Trip!", 
      subtitle: "Get everything you need in one order" },
    { icon: AlertCircle, color: "text-teal-500", bg: "bg-teal-50", border: "border-teal-200",
      title: "Same Assembly Parts", 
      subtitle: "These work together - replace as a set" },
    { icon: Package, color: "text-indigo-500", bg: "bg-indigo-50", border: "border-indigo-200",
      title: "Fleet Manager's Pick", 
      subtitle: "Trusted combo for commercial vehicles" },
  ];

  // Rotate based on part number hash + current hour (changes every hour)
  const currentHour = new Date().getHours();
  const partHash = jpPartNo ? jpPartNo.split('').reduce((a, b) => a + b.charCodeAt(0), 0) : 0;
  const styleIndex = (partHash + currentHour) % wittyStyles.length;
  const selectedStyle = wittyStyles[styleIndex];
  const Icon = selectedStyle.icon;

  return (
    <Card className={`${selectedStyle.bg} ${selectedStyle.border} border-2 ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className={`text-base flex items-center gap-2 ${selectedStyle.color}`}>
          <Icon className="w-5 h-5" />
          <span>{selectedStyle.title}</span>
        </CardTitle>
        <p className="text-sm text-gray-600">{selectedStyle.subtitle}</p>
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {groups.slice(0, 2).map((group, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-white/50">
                {group}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-2">
          {relatedParts.slice(0, 6).map((part) => (
            <div 
              key={part.jp_part_no}
              className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <ProductImage filename={part.image} className="w-12 h-12" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{part.part_name}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600 font-mono">{formatJpWithAlias(part)}</span>
                  {part.category && (
                    <span className="text-xs text-gray-500">• {part.category}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-sm font-bold text-green-700">₹{part.mrp?.toLocaleString() || 0}</span>
                <MrpEffectiveDate part={part} />
                {/* MrpChangeBadge removed 2026-05-30 (see PartCard.js) */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAddToCart(part)}
                  className="h-7 text-xs border-green-500 text-green-600 hover:bg-green-50"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        {relatedParts.length > 6 && (
          <div className="text-center mt-3">
            <span className="text-xs text-gray-500">
              +{relatedParts.length - 6} more related parts
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RelatedParts;
